import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/use-analytics";
import { Mail, Linkedin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { z } from "zod";

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" } as any,
  transition: { duration: 0.6 },
};

const CONTACT_EMAIL = "davidisei@iseiconsultingllc.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/david-isei";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  organization: z.string().trim().max(150).optional().or(z.literal("")),
  inquiryType: z.string().min(1, "Select an inquiry type"),
  message: z.string().trim().min(10, "Please add a brief message").max(2000),
});

const inquiryOptions = [
  "Consulting",
  "Healthcare Strategy",
  "Data & Intelligence",
  "Speaking",
  "Partnerships",
  "Media",
  "Other",
];

export default function ConnectPage() {
  const { toast } = useToast();
  const { trackEvent, trackFormSubmission } = useAnalytics();

  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiryType: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k: keyof typeof form, v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({
        title: "Please review the form",
        description: parsed.error.errors[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/submit-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.name.split(" ")[0] ?? form.name,
          lastName: form.name.split(" ").slice(1).join(" "),
          email: form.email,
          organization: form.organization,
          subject: form.inquiryType,
          message: form.message,
          consent: true,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
      trackFormSubmission("contact", true);
      trackEvent("contact_form_submit", { inquiry_type: form.inquiryType });
      toast({
        title: "Message received",
        description: "Expect a reply within 1–2 business days.",
      });
    } catch {
      trackFormSubmission("contact", false);
      toast({
        title: "Couldn't send message",
        description: `Please email ${CONTACT_EMAIL} directly.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Contact · David Isei"
        description="Contact David Isei. Task Force matters, consulting inquiries, office address, and phone."
        url="https://davidisei.com/connect"
      />

      {/* Hero */}
      <section className="pt-28 pb-20 border-b border-border/30">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground/80 font-medium mb-8">
              Contact
            </p>
            <h1 className="font-display text-3xl md:text-5xl font-semibold text-foreground leading-[1.1] tracking-tight mb-8">
              Contact
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-[1.8] max-w-2xl">
              For Task Force matters, write to{" "}
              <a
                href="mailto:david.isei@hawaiihealthcaretaskforce.org"
                className="text-accent underline underline-offset-2 hover:opacity-80"
              >
                david.isei@hawaiihealthcaretaskforce.org
              </a>
              . For everything else,{" "}
              <a
                href="mailto:davidisei@iseiconsultingllc.com"
                className="text-accent underline underline-offset-2 hover:opacity-80"
              >
                davidisei@iseiconsultingllc.com
              </a>{" "}
              or (808) 489-2924.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-20">
          {/* Form */}
          <motion.div {...fade}>
            {submitted ? (
              <div className="border border-border/40 rounded-sm p-12 bg-card/30">
                <CheckCircle2 className="w-8 h-8 text-accent mb-5" />
                <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                  Received.
                </h2>
                <p className="text-muted-foreground leading-[1.8]">
                  Your note has been delivered and will be read in full. If a
                  response is warranted, you will hear back at{" "}
                  <span className="text-foreground">{form.email}</span> within a
                  few business days.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground/80 font-medium mb-8">
                  Submit an Inquiry
                </p>
                <form onSubmit={handleSubmit} className="space-y-7">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="organization" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Organization</Label>
                      <Input
                        id="organization"
                        value={form.organization}
                        onChange={(e) => update("organization", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inquiryType" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Nature of Inquiry</Label>
                      <Select
                        value={form.inquiryType}
                        onValueChange={(v) => update("inquiryType", v)}
                      >
                        <SelectTrigger id="inquiryType">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {inquiryOptions.map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Context</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="A few sentences on the situation, the parties involved, and what a useful response would look like."
                      className="min-h-[180px] leading-relaxed"
                      required
                    />
                    <p className="text-[11px] text-muted-foreground/70 pt-1">
                      Specifics are appreciated. Generic outreach is unlikely to receive a reply.
                    </p>
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between border-t border-border/30">
                    <p className="text-xs text-muted-foreground pt-4 sm:pt-0">
                      Reviewed personally. Replies sent only where appropriate.
                    </p>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      variant="outline"
                      className="group border-foreground/30 hover:border-accent hover:bg-transparent hover:text-accent"
                      data-cta="true"
                    >
                      {loading ? "Sending…" : "Submit Inquiry"}
                      <ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </motion.div>

          {/* Sidebar */}
          <motion.aside {...fade} className="space-y-12 lg:pt-12">
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground/80 font-medium mb-5">
                Direct Channels
              </h3>
              <div className="space-y-4">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex items-center gap-3 text-foreground hover:text-accent transition-colors"
                >
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{CONTACT_EMAIL}</span>
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-foreground hover:text-accent transition-colors"
                >
                  <Linkedin className="w-4 h-4 text-muted-foreground" />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>

            <div className="border-t border-border/30 pt-8">
              <h3 className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground/80 font-medium mb-5">
                Considered Engagements
              </h3>
              <ul className="space-y-5 text-sm text-muted-foreground leading-[1.75]">
                <li>Advisory work for institutions and senior leadership.</li>
                <li>Healthcare system strategy, architecture, and policy.</li>
                <li>Intelligence and data engagements with material stakes.</li>
                <li>Selective speaking, board, and partnership conversations.</li>
              </ul>
            </div>

            <div className="border-t border-border/30 pt-8">
              <h3 className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground/80 font-medium mb-5">
                A Note on Cadence
              </h3>
              <p className="text-sm text-muted-foreground leading-[1.8]">
                Correspondence is reviewed in batches. A reply, when sent, is
                deliberate. Silence should not be read as discourtesy — only as
                attention directed elsewhere.
              </p>
            </div>

            <div className="border-t border-border/30 pt-8">
              <p className="text-xs text-muted-foreground/70 leading-relaxed">
                Honolulu, Hawaiʻi · Engagements considered nationally and across the Pacific.
              </p>
            </div>
          </motion.aside>
        </div>
      </section>

      {/* Closing Note */}
      <section className="border-t border-border/30 py-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fade}>
            <p className="font-display text-lg md:text-xl text-foreground/90 leading-[1.7] italic">
              "The doors are open. The work is selective."
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.28em] text-muted-foreground/70">
              — David Isei
            </p>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
}
